import {useCallback, useContext, useState} from 'react';
import {DialogProps} from '@mui/material';
import Form from './1_form';
import Confirm from './2_confirm';
import {useTranslation} from 'react-i18next';
import {useForm} from 'react-hook-form';
import {translations} from '../../../../../../../i18n/translations';
import {ProfessionalCaregiverInviteFormType} from '../../../../../../../enums/professional-caregiver-invite-form-type';
import {AffiliationType} from '../../../../../../../backend/enums/affiliation-type';
import {AuthContextType} from '../../../../../../../contexts/auth/types';
import {AuthContext} from '../../../../../../../contexts/auth/context';
import {SnackBarContext} from '../../../../../../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../../../../../../contexts/snack-bar/types';
import {Modal} from '../../../../../../../components/modal/modal';
import {UsersRepository} from '../../../../../../../repositories/users';
import {PostSenior} from '../../../../../../../backend/requests/post-senior';
import {UserType} from '../../../../../../../backend/enums/user-type';
import {Product} from '../../../../../../../backend/enums/product';
import {DEFAULT_FORM_VALIDATIONS} from '../../../../../../../utils/constants';
import moment from 'moment';

export type CollaboratorFormInputs = {
  isActive: boolean;
  name: string;
  displayName: string;
  birthday: Date;
};

const AddSeniorFlow = ({
  inFlow,
  onClose,
  triggerRefetch,
}: {
  inFlow: boolean;
  onClose: () => void;
  triggerRefetch: () => void;
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const DIALOGS_COUNT = 2;

  const authContext: AuthContextType = useContext(AuthContext);
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);

  const entity = {
    id: authContext.state.session?.selectedAffiliation?.entity.id || '',
    name: authContext.state.session?.selectedAffiliation?.entity.name || '',
  };

  const {t} = useTranslation();

  const formInstance = useForm<CollaboratorFormInputs>({
    defaultValues: {
      birthday: undefined,
      isActive: true,
    },
    ...DEFAULT_FORM_VALIDATIONS,
  });

  const nextStep = () => {
    if (currentStep === DIALOGS_COUNT - 1) closeFlow();
    if (currentStep < DIALOGS_COUNT - 1) setCurrentStep((state) => state + 1);
  };

  const closeFlow = useCallback(() => {
    setCurrentStep(0);
    onClose();
    formInstance.reset();
  }, [formInstance, onClose]);

  const createSenior = async () => {
    const {isActive, name, displayName, birthday} = formInstance.getValues();

    if (isSubmitting) return;

    try {
      // Check if the entity is set
      if (!entity) throw new Error('No entity set.');

      setIsSubmitting(true);

      const UTCBirthday = moment(birthday)
        .utc()
        .set({hours: 0, minutes: 0, seconds: 0, milliseconds: 0})
        .toISOString();

      // Request body
      const requestBody: PostSenior = {
        product: Product.Bluebird,
        type: UserType.Senior,
        isActive,
        name,
        displayName: displayName.trim() === '' ? undefined : displayName,
        birthday: UTCBirthday,
      };

      // Create senior
      await UsersRepository.instance.createSenior(entity.id, requestBody);

      // Show toast
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.seniors.root.modals.add_senior_flow.snackbar.success_message, {
          name,
        }),
      });

      nextStep();
      triggerRefetch();
    } catch (error: any) {
      // Log
      console.debug('🔴 Could not send invite.', error);

      // Check if it's a "licenses-number-exceeded" error
      const hasLicenseNumberExceeded: boolean = Boolean(
        error &&
          error.response &&
          error.response.data &&
          Array.isArray(error.response.data.errors) &&
          error.response.data.errors[0] &&
          error.response.data.errors[0].code === 'licenses-number-exceeded'
      );

      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: hasLicenseNumberExceeded
          ? t(translations.user_form.errors.create_active_senior_licenses_number_exceeded_error)
          : t(translations.seniors.root.modals.add_senior_flow.snackbar.success_message),
      });

      closeFlow();
    }

    setIsSubmitting(false);
  };

  const defaultDialogProps = {
    onClose: closeFlow,
    fullWidth: true,
    maxWidth: 'sm',
    preventCloseClick: true,
  } as Partial<DialogProps>;

  return (
    <>
      <Modal
        id="add-senior-form"
        open={inFlow && currentStep === 0}
        title={t(translations.seniors.root.modals.add_senior_flow.form.title)}
        actions={[
          {
            label: t(translations.seniors.root.modals.add_senior_flow.form.cancel),
            type: 'destructive',
            onClick: closeFlow,
          },
          {
            label: t(translations.seniors.root.modals.add_senior_flow.form.add),
            type: 'nondestructive',
            onClick: nextStep,
            disabled: !formInstance.formState.isValid,
          },
        ]}
        {...defaultDialogProps}
      >
        <Form
          formType={
            authContext.state.session?.selectedAffiliation?.type === AffiliationType.Administrator
              ? ProfessionalCaregiverInviteFormType.ProfessionalCaregiver_Administrator
              : ProfessionalCaregiverInviteFormType.ProfessionalCaregiver_Manager
          }
          formInstance={formInstance}
        />
      </Modal>

      <Modal
        id="add-senior-confirm"
        open={inFlow && currentStep === 1}
        title={t(translations.seniors.root.modals.add_senior_flow.confirm.title)}
        actions={[
          {
            label: t(translations.seniors.root.modals.add_senior_flow.confirm.cancel),
            type: 'destructive',
            onClick: closeFlow,
            disabled: isSubmitting,
          },
          {
            label: t(translations.seniors.root.modals.add_senior_flow.confirm.add),
            type: 'nondestructive',
            onClick: createSenior,
            loading: isSubmitting,
          },
        ]}
        {...defaultDialogProps}
      >
        <Confirm
          {...{
            name: formInstance.getValues('displayName'),
          }}
        />
      </Modal>
    </>
  );
};

export default AddSeniorFlow;
